import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  CalendarCheck,
  CreditCard,
  FileText,
  FileSearch,
  FlaskConical,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Upload,
  Receipt,
  ClipboardPlus,
  Stethoscope,
  Settings,
  TestTube2,
  UserRoundCog,
  Users,
  WalletCards,
  Webhook,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { APP_NAME, CLINIC_NAME } from "../config/branding";
import { normalizeRole, visibleNavItems } from "../utils/permissions";
import { isUnderMaintenance } from "../config/maintenance";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/patients", icon: Users, label: "Patients" },
  { path: "/doctors", icon: Stethoscope, label: "Doctors" },
  { path: "/services", icon: ListChecks, label: "Services" },
  { path: "/samples", icon: TestTube2, label: "Samples & Labs" },
  { path: "/report-extract", icon: FileSearch, label: "Report Extract" },
  { path: "/report-standardizer", icon: FlaskConical, label: "Report Standardizer" },
  { path: "/prescriptions", icon: ClipboardPlus, label: "Prescriptions" },
  { path: "/reports", icon: FileText, label: "Reports" },
  { path: "/billing", icon: Receipt, label: "Billing" },
  { path: "/attendance", icon: CalendarCheck, label: "Attendance" },
  { path: "/employees", icon: UserRoundCog, label: "Employees" },
  { path: "/payroll", icon: WalletCards, label: "Payroll" },
  { path: "/expenses", icon: CreditCard, label: "Expenses" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/integrations", icon: Webhook, label: "Integrations" },
  { path: "/imports", icon: Upload, label: "Import Center" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const logoSrc = "/LogoMark.png";

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const centerName = auth.clinic?.name || CLINIC_NAME;
  const allowedNavItems = visibleNavItems(auth.user, navItems);
  const currentPage =
    navItems.find(
      (item) =>
        location.pathname === item.path ||
        (item.path !== "/" && location.pathname.startsWith(item.path)),
    )?.label || "Dashboard";

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      console.error(error);
    } finally {
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-hospital-bg lg:flex">
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-72 border-r border-gray-200 bg-white lg:flex lg:flex-col">
        <div className="border-b border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
              <img src={logoSrc} alt={`${APP_NAME} logo`} className="h-full w-full object-contain p-1" />
            </div>
            <div>
              <h1 className="font-manrope text-base font-bold text-hospital-text-main">
                {APP_NAME}
              </h1>
              <p className="text-xs text-hospital-text-muted">{centerName}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {allowedNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
              {isUnderMaintenance(item.path) && (
                <span className="ml-auto inline-flex items-center rounded-full bg-hospital-warning/10 px-1.5 py-0.5 text-[9px] font-semibold text-hospital-warning ring-1 ring-inset ring-hospital-warning/20">
                  Maint.
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-gray-200 p-4">
          <div className="mb-3 flex items-center gap-3 rounded-lg bg-gray-50 p-3">
            <Building2 className="h-5 w-5 text-hospital-primary" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-hospital-text-main">
                {auth.user?.name || "Local User"}
              </p>
              <p className="text-xs capitalize text-hospital-text-muted">
                {normalizeRole(auth.user?.role) || "offline mode"}{" "}
                {auth.clinic?.scale ? `| ${auth.clinic.scale}` : ""}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      <div className="min-h-screen flex-1 lg:ml-72">
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 px-4 py-4 backdrop-blur lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt={`${APP_NAME} logo`} className="h-10 w-10 rounded-lg object-contain ring-1 ring-gray-200 lg:hidden" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-hospital-text-muted">
                    Clinic Operations
                  </p>
                  <h2 className="font-manrope text-xl font-bold text-hospital-text-main">
                    {currentPage}
                  </h2>
                </div>
              </div>
            </div>
            <span className="text-sm text-hospital-text-muted">
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
          
        </header>
        {/* MOBILE BOTTOM NAVBAR */}

          <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white lg:hidden">
            <div className="grid grid-cols-5">
              {visibleNavItems(auth.user, [
                {
                  path: "/",
                  icon: LayoutDashboard,
                  label: "Home",
                },

                {
                  path: "/patients",
                  icon: Users,
                  label: "Patients",
                },

                {
                  path: "/billing",
                  icon: Receipt,
                  label: "Billing",
                },

                {
                  path: "/samples",
                  icon: TestTube2,
                  label: "Labs",
                },

                {
                  path: "/menu",
                  icon: Settings,
                  label: "More",
                },
              ]).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-1 py-3 text-xs font-medium transition ${
                      isActive ? "text-hospital-primary" : "text-gray-500"
                    }`
                  }
                >
                  <item.icon className="h-5 w-5" />

                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        <main className="p-4 pb-24 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
